/**
 * Cookie Manager Utility
 * Handles cookie consent and management for the application
 */

export interface CookieSettings {
  essential: boolean; // Always true, cannot be changed
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

// Default cookie settings
const defaultSettings: CookieSettings = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false
};

/**
 * Get current cookie settings
 */
export function getCookieSettings(): CookieSettings {
  try {
    const savedSettings = localStorage.getItem('cookieSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      // Ensure essential cookies are always true
      return { ...settings, essential: true };
    }
  } catch (error) {
    console.error('Error reading cookie settings:', error);
  }
  
  return defaultSettings;
}

/**
 * Save cookie settings
 */
export function saveCookieSettings(settings: CookieSettings): void {
  try {
    // Ensure essential cookies are always true
    const finalSettings = { ...settings, essential: true };
    localStorage.setItem('cookieSettings', JSON.stringify(finalSettings));
    localStorage.setItem('cookiesAccepted', 'true');
    
    // Apply settings to active services
    applySettings(finalSettings);
  } catch (error) {
    console.error('Error saving cookie settings:', error);
  }
}

/**
 * Apply cookie settings to active services
 */
function applySettings(settings: CookieSettings): void {
  // Analytics cookies
  if (!settings.analytics) {
    disableAnalytics();
  } else {
    enableAnalytics();
  }
  
  // Marketing cookies
  if (!settings.marketing) {
    disableMarketing();
  } else {
    enableMarketing();
  }
  
  // Preference cookies
  if (!settings.preferences) {
    disablePreferences();
  } else {
    enablePreferences();
  }
}

/**
 * Check if a specific cookie type is enabled
 */
export function isCookieEnabled(type: keyof CookieSettings): boolean {
  const settings = getCookieSettings();
  return settings[type];
}

/**
 * Reset all cookie settings to default
 */
export function resetCookieSettings(): void {
  localStorage.removeItem('cookieSettings');
  localStorage.removeItem('cookiesAccepted');
}

// Helper functions for specific cookie types

function disableAnalytics(): void {
  // Disable analytics services
  // This would typically involve setting opt-out cookies for services like Google Analytics
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'optout_analytics'
    });
  }
}

function enableAnalytics(): void {
  // Enable analytics services
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'optin_analytics'
    });
  }
}

function disableMarketing(): void {
  // Disable marketing cookies
  // This would typically involve setting opt-out cookies for services like Facebook Pixel
  if (window.fbq) {
    window.fbq('consent', 'revoke');
  }
}

function enableMarketing(): void {
  // Enable marketing cookies
  if (window.fbq) {
    window.fbq('consent', 'grant');
  }
}

function disablePreferences(): void {
  // Disable preference cookies
  // This would typically clear any user preference cookies
  // For demonstration purposes, we'll just log this action
  console.log('Preference cookies disabled');
}

function enablePreferences(): void {
  // Enable preference cookies
  console.log('Preference cookies enabled');
}